import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../types/api.types';

export type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

/**
 * Service de gestion des notifications Web Push côté frontend.
 *
 * <p>Étapes du flow :</p>
 * <ol>
 *   <li>{@link #registerServiceWorker} — enregistre le SW {@code /dinki-push-sw.js}
 *       (singleton ; idempotent). À appeler dès le boot de l'app.</li>
 *   <li>{@link #requestPermission} — demande la permission navigateur.</li>
 *   <li>{@link #subscribe} — récupère la clé publique VAPID puis appelle
 *       {@code pushManager.subscribe} et envoie le résultat au backend.</li>
 *   <li>{@link #unsubscribe} — désabonne navigateur et backend.</li>
 * </ol>
 */
@Injectable({ providedIn: 'root' })
export class PushService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/notifications/push`;

  /** État de l'abonnement courant (true si souscrit). */
  private readonly _subscribed$ = new BehaviorSubject<boolean>(false);
  readonly subscribed$ = this._subscribed$.asObservable();

  /** Permission courante du navigateur. */
  readonly permission$ = new BehaviorSubject<PushPermission>(
    this.currentPermission(),
  );

  /** Référence du SW une fois enregistré. */
  private registration: ServiceWorkerRegistration | null = null;

  // ─── API publique ────────────────────────────────────────────────────

  /**
   * Enregistre le service worker Dinki. Idempotent.
   * Retourne true si supporté, false si le navigateur ne le supporte pas.
   */
  async registerServiceWorker(): Promise<boolean> {
    if (!this.isSupported()) {
      this.permission$.next('unsupported');
      return false;
    }
    try {
      const reg = await navigator.serviceWorker.register('/dinki-push-sw.js', {
        scope: '/',
      });
      this.registration = reg;
      // Met à jour l'état si déjà souscrit (cas reload de page).
      const existing = await reg.pushManager.getSubscription();
      this._subscribed$.next(!!existing);
      return true;
    } catch (e) {
      // Pas bloquant — l'app fonctionne sans push.
      console.warn('Dinki : enregistrement Service Worker push échoué', e);
      return false;
    }
  }

  /** Demande la permission navigateur (si pas déjà accordée/refusée). */
  async requestPermission(): Promise<PushPermission> {
    if (!this.isSupported()) return 'unsupported';
    const result = await Notification.requestPermission();
    this.permission$.next(result as PushPermission);
    return result as PushPermission;
  }

  /**
   * Crée la souscription navigateur + envoie au backend.
   *
   * @throws si la permission est refusée ou si le SW n'est pas enregistré.
   */
  async subscribe(label?: string): Promise<void> {
    if (!this.isSupported()) throw new Error('Web Push non supporté');
    if (!this.registration) {
      const ok = await this.registerServiceWorker();
      if (!ok || !this.registration)
        throw new Error('Service Worker indisponible');
    }
    let perm = this.currentPermission();
    if (perm === 'default') perm = await this.requestPermission();
    if (perm !== 'granted') throw new Error('Permission refusée');

    // 1. Récupérer la clé publique VAPID
    const pk = await firstValueFrom(
      this.http
        .get<ApiResponse<{ publicKey: string }>>(`${this.base}/public-key`)
        .pipe(map((r) => r.data.publicKey)),
    );
    if (!pk) throw new Error('Clé VAPID non configurée côté backend');

    // 2. Subscribe via le SW
    // Cast via `unknown` : `new Uint8Array(...)` est typé `Uint8Array<ArrayBufferLike>`
    // (union ArrayBuffer | SharedArrayBuffer) alors que `pushManager.subscribe`
    // attend strictement `BufferSource`. Au runtime notre helper alloue toujours
    // un ArrayBuffer régulier, donc le cast est sûr.
    const sub = await this.registration!.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        pk,
      ) as unknown as BufferSource,
    });

    // 3. Envoyer au backend
    const json = sub.toJSON();
    await firstValueFrom(
      this.http.post(`${this.base}/subscribe`, {
        endpoint: json.endpoint,
        p256dh: json.keys?.['p256dh'],
        auth: json.keys?.['auth'],
        userAgent: navigator.userAgent,
        label: label || null,
      }),
    );

    this._subscribed$.next(true);
  }

  /** Désabonne (navigateur + backend). */
  async unsubscribe(): Promise<void> {
    if (!this.registration) return;
    const sub = await this.registration.pushManager.getSubscription();
    if (!sub) {
      this._subscribed$.next(false);
      return;
    }
    const endpoint = sub.endpoint;
    try {
      await sub.unsubscribe();
    } catch {
      /* ignore */
    }
    try {
      await firstValueFrom(
        this.http.delete(`${this.base}/subscribe`, {
          params: { endpoint },
        }),
      );
    } catch {
      /* le backend purgera plus tard via 410 */
    }
    this._subscribed$.next(false);
  }

  /** True si la souscription actuelle est active. */
  async isSubscribed(): Promise<boolean> {
    if (!this.registration) return false;
    const sub = await this.registration.pushManager.getSubscription();
    return !!sub;
  }

  /** True si le navigateur supporte le Web Push. */
  isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  // ─── interne ──────────────────────────────────────────────────────────

  private currentPermission(): PushPermission {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission as PushPermission;
  }
}

/**
 * Convertit une clé VAPID au format base64url en Uint8Array — format attendu
 * par {@code pushManager.subscribe}.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
